const normalizeText = (value = '') =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const numberValue = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

class JD1AmaguauPersonalizer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.jd1Ready === 'true') return;
    this.dataset.jd1Ready = 'true';

    this.variants = this.#parseJSON('[data-jd1-variants]', []);
    this.optionNames = this.#parseJSON('[data-jd1-options]', []);

    this.previewStage = this.querySelector('[data-jd1-preview-stage]');
    this.engravingArea = this.querySelector('[data-jd1-engraving-area]');
    this.previewTextBlock = this.querySelector('[data-jd1-preview-text-block]');
    this.previewLines = [...this.querySelectorAll('[data-jd1-preview-line]')];
    this.finishImages = this.querySelectorAll('[data-jd1-finish-image]');
    this.photoStep = this.querySelector('[data-jd1-photo-step]');
    this.textStep = this.querySelector('[data-jd1-text-step]');
    this.textStepNumber = this.querySelector('[data-jd1-text-step-number]');
    this.textStepHeading = this.querySelector('[data-jd1-text-heading]');
    this.textStepDescription = this.querySelector('[data-jd1-text-description]');
    this.defaultTextStepHeading = this.textStepHeading?.textContent || 'Escribe su mensaje';
    this.defaultTextStepDescription = this.textStepDescription?.textContent || 'Nombres, fechas o frases cortas funcionan mejor.';
    this.frontControls = this.querySelector('[data-jd1-front-controls]');
    this.photoField = this.querySelector('[data-jd1-photo-field]');
    this.photoInput = this.querySelector('[data-jd1-photo-input]');
    this.photoScaleInput = this.querySelector('[data-jd1-photo-scale]');
    this.photoOffsetYInput = this.querySelector('[data-jd1-photo-offset-y]');
    this.photoScaleProperty = this.querySelector('[data-jd1-photo-scale-property]');
    this.photoOffsetYProperty = this.querySelector('[data-jd1-photo-offset-y-property]');
    this.photoPreview = this.querySelector('[data-jd1-photo-preview]');
    this.photoPreviewImage = this.querySelector('[data-jd1-photo-preview-image]');
    this.variantInput = this.querySelector('[data-jd1-variant-id]');
    this.addButton = this.querySelector('[data-jd1-add-button]');
    this.priceNodes = this.querySelectorAll('[data-jd1-price], [data-jd1-button-price]');
    this.errorNode = this.querySelector('[data-jd1-error]');
    this.backControls = this.querySelector('[data-jd1-back-controls]');

    this.frontInputs = [...this.querySelectorAll('[data-jd1-front-line-input]')];
    this.backInputs = [...this.querySelectorAll('[data-jd1-back-line-input]')];
    this.frontSizeInputs = [...this.querySelectorAll('[data-jd1-front-line-size]')];
    this.backSizeInputs = [...this.querySelectorAll('[data-jd1-back-line-size]')];
    this.frontFontInputs = [...this.querySelectorAll('[data-jd1-front-line-font]')];
    this.backFontInputs = [...this.querySelectorAll('[data-jd1-back-line-font]')];
    this.frontGapInput = this.querySelector('[data-jd1-front-gap]');
    this.backGapInput = this.querySelector('[data-jd1-back-gap]');
    this.frontOffsetInput = this.querySelector('[data-jd1-front-offset]');
    this.backOffsetInput = this.querySelector('[data-jd1-back-offset]');
    this.photoObjectUrl = '';
    this.handleWindowResize = () => this.#fitPreviewContent();

    this.state = {
      target: 'pet',
      shape: 'round',
      size: '2',
      finish: 'silver',
      engraving: 'text',
      faces: 'one',
      previewSide: 'front',
      frontLines: '1',
      backLines: '1',
      frontAlign: 'center',
      backAlign: 'center',
    };

    this.querySelectorAll('[data-jd1-choice]').forEach((button) => {
      button.addEventListener('click', () => this.#selectChoice(button));
    });

    this.querySelectorAll('[data-preview-side]').forEach((button) => {
      button.addEventListener('click', () => this.#selectPreviewSide(button));
    });

    [...this.frontInputs, ...this.backInputs].forEach((input) => {
      input.addEventListener('input', () => this.#updateText());
    });

    this.photoInput?.addEventListener('change', () => this.#handlePhotoChange());
    [this.photoScaleInput, this.photoOffsetYInput].filter(Boolean).forEach((input) => {
      input.addEventListener('input', () => this.#updatePhotoTransform());
    });

    [...this.frontFontInputs, ...this.backFontInputs].forEach((select) => {
      select.addEventListener('change', () => this.#updateText());
    });

    [
      ...this.frontSizeInputs,
      ...this.backSizeInputs,
      this.frontGapInput,
      this.backGapInput,
      this.frontOffsetInput,
      this.backOffsetInput,
    ].filter(Boolean).forEach((input) => {
      input.addEventListener('input', () => this.#updateText());
    });

    const form = this.querySelector('form');
    form?.addEventListener('submit', (event) => {
      const variant = this.#findVariant();
      if (!variant || !variant.available) {
        event.preventDefault();
        this.#showError('Esta configuración todavía no tiene una variante disponible.');
        return;
      }

      const hasBack = this.state.faces === 'two';
      const textOnly = this.state.engraving === 'text';
      const photoText = this.state.engraving === 'photo_text';
      const photoOnly = this.state.engraving === 'photo_only';
      const photoMode = photoText || photoOnly;

      if (textOnly && !this.frontInputs[0]?.value.trim()) {
        event.preventDefault();
        this.frontInputs[0]?.focus();
        this.#showError('Escribe la primera línea del frente antes de agregar la pieza.');
        return;
      }

      if (hasBack && (textOnly || photoText) && !this.backInputs[0]?.value.trim()) {
        event.preventDefault();
        this.backInputs[0]?.focus();
        this.#showError('Escribe la primera línea del reverso antes de agregar la pieza.');
        return;
      }

      if (photoMode && !this.photoInput?.files?.length) {
        event.preventDefault();
        this.photoInput?.focus();
        this.#showError('Sube la fotografía que quieres grabar antes de agregar la pieza.');
      }
    }, true);

    this.#syncTargetShapes();
    this.#syncTargetSizes();
    this.#syncTargetFinishes();
    this.#syncPhotoAvailability();
    this.#syncFaces();
    this.#syncTextRows('front');
    this.#syncTextRows('back');
    this.#updateAll();

    window.addEventListener('resize', this.handleWindowResize, { passive: true });
    if ('ResizeObserver' in window && this.previewStage) {
      this.previewResizeObserver = new ResizeObserver(() => this.#fitPreviewContent());
      this.previewResizeObserver.observe(this.previewStage);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleWindowResize);
    this.previewResizeObserver?.disconnect();
    if (this.photoObjectUrl) {
      URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = '';
    }
  }

  #parseJSON(selector, fallback) {
    const node = this.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.warn('JD1 Amaguau: JSON inválido', error);
      return fallback;
    }
  }

  #selectChoice(button) {
    if (button.disabled) return;
    const type = button.dataset.jd1Choice;
    if (!type) return;

    const group = this.querySelector(`[data-jd1-group="${type}"]`);
    group?.querySelectorAll(`[data-jd1-choice="${type}"]`).forEach((candidate) => {
      const active = candidate === button;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.state[type] = button.dataset.value || '';

    if (type === 'target') {
      this.#syncTargetShapes();
      this.#syncTargetSizes();
      this.#syncTargetFinishes();
      this.#syncPhotoAvailability();
    }
    if (type === 'engraving') this.#syncEngraving();
    if (type === 'faces') this.#syncFaces();
    if (type === 'frontLines') this.#syncTextRows('front');
    if (type === 'backLines') this.#syncTextRows('back');

    this.#updateAll();
  }

  #syncTargetShapes() {
    const target = this.state.target;
    const shapeButtons = [...this.querySelectorAll('[data-jd1-choice="shape"]')];

    shapeButtons.forEach((button) => {
      const supportedTargets = (button.dataset.target || 'both').split(',').map((value) => value.trim());
      button.hidden = !(supportedTargets.includes('both') || supportedTargets.includes(target));
    });

    let active = shapeButtons.find((button) => !button.hidden && button.classList.contains('is-active'));
    if (!active) active = shapeButtons.find((button) => !button.hidden);

    if (active) {
      shapeButtons.forEach((button) => {
        const selected = button === active;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.state.shape = active.dataset.value || 'round';
    }

    const visible = shapeButtons.filter((button) => !button.hidden);
    this.querySelector('[data-jd1-group="shape"]')?.classList.toggle('is-single', visible.length === 1);
  }

  #syncTargetSizes() {
    const target = this.state.target;
    const buttons = [...this.querySelectorAll('[data-jd1-choice="size"]')];
    buttons.forEach((button) => {
      const supportedTargets = (button.dataset.target || 'both').split(',').map((value) => value.trim());
      button.hidden = !(supportedTargets.includes('both') || supportedTargets.includes(target));
    });

    let active = buttons.find((button) => !button.hidden && button.classList.contains('is-active'));
    if (!active) active = buttons.find((button) => !button.hidden);
    buttons.forEach((button) => {
      const selected = button === active;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    if (active) this.state.size = active.dataset.value || '2';

    const visible = buttons.filter((button) => !button.hidden);
    const group = this.querySelector('[data-jd1-group="size"]');
    group?.classList.toggle('is-two', visible.length === 2);
  }

  #syncTargetFinishes() {
    const target = this.state.target;
    const buttons = [...this.querySelectorAll('[data-jd1-choice="finish"]')];
    buttons.forEach((button) => {
      const supportedTargets = (button.dataset.target || 'both').split(',').map((value) => value.trim());
      button.hidden = !(supportedTargets.includes('both') || supportedTargets.includes(target));
    });

    let active = buttons.find((button) => !button.hidden && button.classList.contains('is-active'));
    if (!active) active = buttons.find((button) => !button.hidden);
    buttons.forEach((button) => {
      const selected = button === active;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    if (active) this.state.finish = active.dataset.value || 'silver';

    const visible = buttons.filter((button) => !button.hidden);
    this.querySelector('[data-jd1-group="finish"]')?.classList.toggle('is-single', visible.length === 1);
  }

  #syncPhotoAvailability() {
    const human = this.state.target === 'human';

    if (this.photoStep) this.photoStep.hidden = !human;
    if (this.textStepNumber) this.textStepNumber.textContent = human ? '07' : '06';

    if (!human) this.state.engraving = 'text';

    this.#syncEngravingOptions();
    this.#syncEngraving();
  }

  #syncEngravingOptions() {
    const human = this.state.target === 'human';
    const hasBack = this.state.faces === 'two';
    const group = this.querySelector('[data-jd1-group="engraving"]');
    const textButton = this.querySelector('[data-jd1-choice="engraving"][data-value="text"]');
    const photoTextButton = this.querySelector('[data-jd1-choice="engraving"][data-value="photo_text"]');
    const photoOnlyButton = this.querySelector('[data-jd1-choice="engraving"][data-value="photo_only"]');
    const textLabel = this.querySelector('[data-jd1-engraving-text-label]');

    if (group) group.dataset.faces = hasBack ? 'two' : 'one';

    if (textLabel && textButton) {
      const label = hasBack ? (textButton.dataset.labelTwo || 'Solo textos') : (textButton.dataset.labelOne || 'Solo texto');
      textLabel.textContent = label;
      textButton.dataset.label = label;
    }

    const setAvailability = (button, available) => {
      if (!button) return;
      button.hidden = !available;
      button.disabled = !available;
      button.classList.toggle('is-unavailable', !available);
      button.setAttribute('aria-hidden', available ? 'false' : 'true');
      if (available) button.removeAttribute('tabindex');
      else button.setAttribute('tabindex', '-1');
    };

    setAvailability(textButton, human);
    setAvailability(photoTextButton, human && hasBack);
    setAvailability(photoOnlyButton, human && !hasBack);

    if (!human) {
      this.state.engraving = 'text';
    } else if (hasBack && this.state.engraving === 'photo_only') {
      this.state.engraving = 'photo_text';
    } else if (!hasBack && this.state.engraving === 'photo_text') {
      this.state.engraving = 'photo_only';
    }

    const availableButtons = [textButton, photoTextButton, photoOnlyButton]
      .filter((button) => button && !button.hidden && !button.disabled);

    if (!availableButtons.some((button) => button.dataset.value === this.state.engraving)) {
      this.state.engraving = availableButtons[0]?.dataset.value || 'text';
    }

    [textButton, photoTextButton, photoOnlyButton].filter(Boolean).forEach((button) => {
      const active = !button.hidden && button.dataset.value === this.state.engraving;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    group?.classList.toggle('is-two', availableButtons.length === 2);
  }

  #syncEngraving() {
    const human = this.state.target === 'human';
    const hasBack = this.state.faces === 'two';
    const photoText = human && hasBack && this.state.engraving === 'photo_text';
    const photoOnly = human && !hasBack && this.state.engraving === 'photo_only';
    const photoMode = photoText || photoOnly;

    if (this.photoField) this.photoField.hidden = !photoMode;
    if (this.photoInput) {
      this.photoInput.disabled = !photoMode;
      this.photoInput.required = photoMode;
    }

    [this.photoScaleInput, this.photoOffsetYInput].filter(Boolean).forEach((input) => {
      input.disabled = !photoMode;
    });
    if (this.photoScaleProperty) this.photoScaleProperty.disabled = !photoMode;
    if (this.photoOffsetYProperty) this.photoOffsetYProperty.disabled = !photoMode;

    if (this.previewStage) this.previewStage.dataset.engraving = photoMode ? this.state.engraving : 'text';
    if (this.frontInputs[0]) this.frontInputs[0].required = this.state.engraving === 'text';
    if (this.backInputs[0]) this.backInputs[0].required = hasBack && (this.state.engraving === 'text' || photoText);

    this.#syncTextAvailability();
    this.#updatePhotoTransform();
    this.#updatePhotoPreviewVisibility();
  }

  #handlePhotoChange() {
    const file = this.photoInput?.files?.[0];
    if (this.photoObjectUrl) {
      URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = '';
    }

    if (!file) {
      if (this.photoPreviewImage) this.photoPreviewImage.removeAttribute('src');
      this.#setText('[data-jd1-photo-name]', 'Ningún archivo seleccionado');
      this.#updatePhotoPreviewVisibility();
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.photoInput.value = '';
      this.#showError('El archivo debe ser una imagen JPG, PNG o WEBP.');
      this.#updatePhotoPreviewVisibility();
      return;
    }

    this.photoObjectUrl = URL.createObjectURL(file);
    if (this.photoPreviewImage) this.photoPreviewImage.src = this.photoObjectUrl;
    this.#setText('[data-jd1-photo-name]', file.name);
    this.#hideError();
    this.#updatePhotoPreviewVisibility();
  }

  #updatePhotoPreviewVisibility() {
    if (!this.photoPreview) return;
    const photoMode = this.state.engraving === 'photo_text' || this.state.engraving === 'photo_only';
    const visible = this.state.target === 'human' && photoMode && this.state.previewSide === 'front' && Boolean(this.photoPreviewImage?.getAttribute('src'));
    this.photoPreview.hidden = !visible;
    this.#fitPreviewContent();
  }

  #updatePhotoTransform() {
    const scalePercent = numberValue(this.photoScaleInput?.value, 100);
    const offsetY = numberValue(this.photoOffsetYInput?.value, 0);
    const scale = Math.max(0.7, Math.min(1.8, scalePercent / 100));

    if (this.photoPreview) {
      this.photoPreview.style.setProperty('--jd1-photo-scale', scale.toString());
      this.photoPreview.style.setProperty('--jd1-photo-offset-y', `${offsetY}px`);
    }

    if (this.photoScaleProperty) this.photoScaleProperty.value = scalePercent.toString();
    if (this.photoOffsetYProperty) this.photoOffsetYProperty.value = offsetY.toString();
    this.#setText('[data-jd1-photo-scale-output]', `${scalePercent}%`);
    this.#setText('[data-jd1-photo-offset-y-output]', `${offsetY}px`);
  }

  #syncFaces() {
    const hasBack = this.state.faces === 'two';

    const backPreviewButton = this.querySelector('[data-preview-side="back"]');
    if (backPreviewButton) backPreviewButton.hidden = !hasBack;

    if (!hasBack && this.state.previewSide === 'back') {
      const frontButton = this.querySelector('[data-preview-side="front"]');
      if (frontButton) this.#selectPreviewSide(frontButton);
    }

    this.#syncEngravingOptions();
    this.#syncEngraving();
  }

  #syncTextAvailability() {
    const human = this.state.target === 'human';
    const hasBack = this.state.faces === 'two';
    const textOnly = this.state.engraving === 'text';
    const photoText = human && hasBack && this.state.engraving === 'photo_text';
    const showFrontText = textOnly;
    const showBackText = hasBack && (textOnly || photoText);
    const showTextStep = showFrontText || showBackText;

    if (this.textStep) this.textStep.hidden = !showTextStep;
    if (this.frontControls) this.frontControls.hidden = !showFrontText;
    if (this.backControls) this.backControls.hidden = !showBackText;

    if (this.textStepHeading) {
      this.textStepHeading.textContent = photoText
        ? 'Personaliza el reverso'
        : (hasBack ? 'Personaliza las dos caras' : this.defaultTextStepHeading);
    }
    if (this.textStepDescription) {
      this.textStepDescription.textContent = photoText
        ? 'Escribe el mensaje que irá en la segunda cara de la pieza.'
        : (hasBack ? 'Escribe y ajusta el texto que irá en cada lado.' : this.defaultTextStepDescription);
    }

    this.#syncTextRows('front');
    this.#syncTextRows('back');
  }

  #syncTextRows(side) {
    const isFront = side === 'front';
    const count = numberValue(this.state[`${side}Lines`], 1);
    const hasBack = this.state.faces === 'two';
    const rows = [...this.querySelectorAll(`[data-jd1-${side}-line-row]`)];
    const inputs = isFront ? this.frontInputs : this.backInputs;
    const sizes = isFront ? this.frontSizeInputs : this.backSizeInputs;
    const fonts = isFront ? this.frontFontInputs : this.backFontInputs;
    const human = this.state.target === 'human';
    const textOnly = this.state.engraving === 'text';
    const photoText = human && hasBack && this.state.engraving === 'photo_text';
    const enabledFace = isFront ? textOnly : hasBack && (textOnly || photoText);

    rows.forEach((row, index) => {
      const lineNumber = index + 1;
      const active = enabledFace && lineNumber <= count;
      row.hidden = lineNumber > count;
      if (inputs[index]) inputs[index].disabled = !active;
      if (sizes[index]) sizes[index].disabled = !active;
      if (fonts[index]) fonts[index].disabled = !active;
    });

    if (!isFront) {
      this.querySelectorAll('[data-jd1-group="backLines"] button, [data-jd1-group="backAlign"] button').forEach((button) => {
        button.disabled = !enabledFace;
      });
      [this.backGapInput, this.backOffsetInput].filter(Boolean).forEach((input) => {
        input.disabled = !enabledFace;
      });
    }

    const summary = this.querySelector(`[data-jd1-${side}-summary-property]`);
    const linesProperty = this.querySelector(`[data-jd1-${side}-lines-property]`);
    const gapProperty = this.querySelector(`[data-jd1-${side}-gap-property]`);
    const offsetProperty = this.querySelector(`[data-jd1-${side}-offset-property]`);
    const alignProperty = this.querySelector(`[data-jd1-${side}-align-property]`);
    const sizeProperties = [...this.querySelectorAll(`[data-jd1-${side}-size-property]`)];
    const fontProperties = [...this.querySelectorAll(`[data-jd1-${side}-font-property]`)];

    [summary, linesProperty, gapProperty, offsetProperty, alignProperty, ...sizeProperties, ...fontProperties].filter(Boolean).forEach((input) => {
      input.disabled = !enabledFace;
    });
  }

  #selectPreviewSide(button) {
    this.querySelectorAll('[data-preview-side]').forEach((candidate) => {
      const active = candidate === button;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.state.previewSide = button.dataset.previewSide || 'front';
    this.#updatePhotoPreviewVisibility();
    this.#updateText();
  }

  #activeButton(type) {
    return this.querySelector(`[data-jd1-group="${type}"] [data-jd1-choice="${type}"].is-active`);
  }

  #updateAll() {
    if (this.previewStage) {
      this.previewStage.dataset.shape = this.state.shape;
      this.previewStage.dataset.size = this.state.size;
      this.previewStage.dataset.finish = this.state.finish;
      this.previewStage.dataset.engraving = this.state.engraving;
      this.previewStage.dataset.side = this.state.previewSide;
    }

    this.#updateFinishImages();
    this.#updatePhotoTransform();
    this.#updatePhotoPreviewVisibility();
    this.#updateProperties();
    this.#updateSummary();
    this.#updateText();
    this.#updateVariant();
  }

  #updateFinishImages() {
    const finish = this.state.finish === 'gold' ? 'gold' : 'silver';
    this.finishImages?.forEach((image) => {
      const src = finish === 'gold' ? image.dataset.srcGold : image.dataset.srcSilver;
      if (src && image.getAttribute('src') !== src) image.setAttribute('src', src);
    });
  }

  #updateProperties() {
    const targetButton = this.#activeButton('target');
    const shapeButton = this.#activeButton('shape');
    const sizeButton = this.#activeButton('size');
    const finishButton = this.#activeButton('finish');
    const engravingButton = this.#activeButton('engraving');
    const facesButton = this.#activeButton('faces');

    this.#setValue('[data-jd1-property-shape]', this.state.shape);
    this.#setValue('[data-jd1-property-size]', this.state.size);
    this.#setValue('[data-jd1-property-finish]', this.state.finish);
    this.#setValue('[data-jd1-property-engraving]', this.state.engraving);
    this.#setValue('[data-jd1-property-faces]', this.state.faces);
    this.#setValue('[data-jd1-property-target-label]', targetButton?.dataset.label || 'Para mi mascota');
    this.#setValue('[data-jd1-property-shape-label]', shapeButton?.dataset.label || 'Placa circular');
    this.#setValue('[data-jd1-property-size-label]', sizeButton?.dataset.label || '2 cm');
    this.#setValue('[data-jd1-property-finish-label]', finishButton?.dataset.label || 'Plateado (acero inoxidable)');
    this.#setValue('[data-jd1-property-engraving-label]', engravingButton?.dataset.label || 'Solo texto');
    this.#setValue('[data-jd1-property-faces-label]', facesButton?.dataset.label || 'Una cara');
  }

  #updateSummary() {
    const targetButton = this.#activeButton('target');
    const shapeButton = this.#activeButton('shape');
    const sizeButton = this.#activeButton('size');
    const finishButton = this.#activeButton('finish');
    const facesButton = this.#activeButton('faces');

    this.#setText('[data-jd1-summary-target]', targetButton?.dataset.label || 'Para mi mascota');
    this.#setText('[data-jd1-summary-shape]', shapeButton?.dataset.label || 'Placa circular');
    this.#setText('[data-jd1-summary-size]', sizeButton?.dataset.label || '2 cm');
    this.#setText('[data-jd1-summary-finish]', finishButton?.dataset.label || 'Plateado (acero inoxidable)');
    this.#setText('[data-jd1-summary-faces]', facesButton?.dataset.label || 'Una cara');
  }

  #faceSettings(side) {
    const isFront = side === 'front';
    return {
      inputs: isFront ? this.frontInputs : this.backInputs,
      sizes: isFront ? this.frontSizeInputs : this.backSizeInputs,
      fonts: isFront ? this.frontFontInputs : this.backFontInputs,
      gapInput: isFront ? this.frontGapInput : this.backGapInput,
      offsetInput: isFront ? this.frontOffsetInput : this.backOffsetInput,
      count: numberValue(this.state[`${side}Lines`], 1),
      align: this.state[`${side}Align`] || 'center',
      fallback: isFront ? 'Luna' : 'Tu mensaje',
    };
  }

  #updateText() {
    ['front', 'back'].forEach((side) => this.#updateFaceProperties(side));

    const side = this.state.previewSide === 'back' ? 'back' : 'front';
    const settings = this.#faceSettings(side);
    const lineValues = settings.inputs.map((input) => input?.value.trim() || '');

    this.previewLines.forEach((line, index) => {
      const active = index < settings.count;
      line.hidden = !active;
      if (!active) return;

      const photoFront = side === 'front' && (this.state.engraving === 'photo_only' || this.state.engraving === 'photo_text');
      if (photoFront) {
        line.hidden = true;
        line.textContent = '';
        return;
      }

      const fallback = index === 0 ? settings.fallback : '';
      line.textContent = lineValues[index] || fallback;
      const size = numberValue(settings.sizes[index]?.value, 32);
      const font = settings.fonts[index]?.value || 'vietnam';
      line.dataset.baseSize = `${size}`;
      line.style.fontSize = `${size}px`;
      line.dataset.font = font;
    });

    if (this.previewTextBlock) {
      this.previewTextBlock.dataset.lines = settings.count.toString();
      this.previewTextBlock.dataset.align = settings.align;
      this.previewTextBlock.style.textAlign = settings.align;
      this.previewTextBlock.dataset.baseGap = `${numberValue(settings.gapInput?.value, 8)}`;
      this.previewTextBlock.style.setProperty('--jd1-preview-line-gap', `${numberValue(settings.gapInput?.value, 8)}px`);
      this.previewTextBlock.style.setProperty('--jd1-preview-offset', `${numberValue(settings.offsetInput?.value, 0)}px`);
    }

    this.#fitPreviewContent();
    this.#updateTextOutputs('front');
    this.#updateTextOutputs('back');
  }

  #fitPreviewContent() {
    if (!this.previewTextBlock || !this.previewStage) return;

    const activeLines = this.previewLines.filter((line) => !line.hidden);
    if (!activeLines.length) return;

    const boundsTarget = this.engravingArea || this.previewStage;
    const rect = boundsTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const baseGap = numberValue(this.previewTextBlock.dataset.baseGap, 8);
    const baseScale = this.state.shape === 'bone' ? 0.84 : 0.88;

    const applyScale = (scale) => {
      activeLines.forEach((line) => {
        const base = numberValue(line.dataset.baseSize, 32);
        const scaled = Math.max(11, Math.round(base * scale));
        line.style.fontSize = `${scaled}px`;
      });
      this.previewTextBlock.style.setProperty('--jd1-preview-line-gap', `${Math.max(0, Math.round(baseGap * Math.max(scale, 0.7)))}px`);
    };

    applyScale(baseScale);

    let scale = baseScale;
    while (scale > 0.42) {
      const tooWide = activeLines.some((line) => line.scrollWidth > line.clientWidth + 1);
      const tooTall = this.previewTextBlock.scrollHeight > this.previewTextBlock.clientHeight + 1;
      if (!tooWide && !tooTall) break;
      scale -= 0.04;
      applyScale(scale);
    }
  }

  #updateFaceProperties(side) {
    const settings = this.#faceSettings(side);
    const human = this.state.target === 'human';
    const hasBack = this.state.faces === 'two';
    const textOnly = this.state.engraving === 'text';
    const photoText = human && hasBack && this.state.engraving === 'photo_text';
    const enabled = side === 'front' ? textOnly : hasBack && (textOnly || photoText);
    const values = settings.inputs.slice(0, settings.count).map((input) => input?.value.trim() || '').filter(Boolean);

    const summary = this.querySelector(`[data-jd1-${side}-summary-property]`);
    const linesProperty = this.querySelector(`[data-jd1-${side}-lines-property]`);
    const gapProperty = this.querySelector(`[data-jd1-${side}-gap-property]`);
    const offsetProperty = this.querySelector(`[data-jd1-${side}-offset-property]`);
    const alignProperty = this.querySelector(`[data-jd1-${side}-align-property]`);

    if (summary) {
      summary.value = values.join(' · ');
      summary.disabled = !enabled;
    }
    if (linesProperty) {
      linesProperty.value = settings.count.toString();
      linesProperty.disabled = !enabled;
    }
    if (gapProperty) {
      gapProperty.value = numberValue(settings.gapInput?.value, 8).toString();
      gapProperty.disabled = !enabled;
    }
    if (offsetProperty) {
      offsetProperty.value = numberValue(settings.offsetInput?.value, 0).toString();
      offsetProperty.disabled = !enabled;
    }
    if (alignProperty) {
      alignProperty.value = settings.align;
      alignProperty.disabled = !enabled;
    }

    settings.sizes.forEach((input, index) => {
      const property = this.querySelector(`[data-jd1-${side}-size-property="${index + 1}"]`);
      if (property) {
        property.value = numberValue(input?.value, 32).toString();
        property.disabled = !enabled || index >= settings.count;
      }
    });

    settings.fonts.forEach((select, index) => {
      const property = this.querySelector(`[data-jd1-${side}-font-property="${index + 1}"]`);
      if (property) {
        property.value = select?.value || 'vietnam';
        property.disabled = !enabled || index >= settings.count;
      }
    });
  }

  #updateTextOutputs(side) {
    const settings = this.#faceSettings(side);
    settings.inputs.forEach((input, index) => {
      this.#setText(`[data-jd1-${side}-count="${index + 1}"]`, (input?.value || '').length.toString());
      this.#setText(`[data-jd1-${side}-size-output="${index + 1}"]`, `${numberValue(settings.sizes[index]?.value, 32)}px`);
    });
    this.#setText(`[data-jd1-${side}-gap-output]`, `${numberValue(settings.gapInput?.value, 8)}px`);
    this.#setText(`[data-jd1-${side}-offset-output]`, `${numberValue(settings.offsetInput?.value, 0)}px`);
  }

  #selectedVariantValues() {
    return {
      [normalizeText(this.dataset.targetOption)]: this.#activeButton('target')?.dataset.variantValue || '',
      [normalizeText(this.dataset.shapeOption)]: this.#activeButton('shape')?.dataset.variantValue || '',
      [normalizeText(this.dataset.sizeOption)]: this.#activeButton('size')?.dataset.variantValue || '',
      [normalizeText(this.dataset.finishOption)]: this.#activeButton('finish')?.dataset.variantValue || '',
      [normalizeText(this.dataset.facesOption)]: this.#activeButton('faces')?.dataset.variantValue || '',
    };
  }

  #findVariant() {
    if (!Array.isArray(this.variants) || this.variants.length === 0) return null;
    if (this.variants.length === 1) return this.variants[0];

    const desired = this.#selectedVariantValues();
    const optionNames = Array.isArray(this.optionNames) ? this.optionNames : [];

    const exact = this.variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      return variant.options.every((value, index) => {
        const optionName = normalizeText(optionNames[index]);
        const expected = desired[optionName];
        if (!expected) return true;
        return normalizeText(value) === normalizeText(expected);
      });
    });

    if (exact) return exact;

    const recognizedNames = new Set(optionNames.map(normalizeText));
    const requiredValues = Object.entries(desired)
      .filter(([name, value]) => recognizedNames.has(name) && value)
      .map(([, value]) => normalizeText(value));

    return this.variants.find((variant) => {
      const haystack = normalizeText([variant.title, ...(variant.options || [])].join(' '));
      return requiredValues.every((value) => haystack.includes(value));
    }) || null;
  }

  #updateVariant() {
    const variant = this.#findVariant();
    const available = Boolean(variant?.available);

    if (this.variantInput && variant?.id) this.variantInput.value = variant.id;

    const price = variant ? this.#formatMoney(variant.price) : '—';
    this.priceNodes.forEach((node) => { node.textContent = price; });

    if (this.addButton) this.addButton.disabled = !available;

    const label = this.querySelector('[data-jd1-button-label]');
    if (label) {
      label.textContent = variant && !variant.available
        ? 'Configuración agotada'
        : (label.dataset.defaultLabel || 'Agregar al carrito');
    }

    if (!variant && this.variants.length > 0) {
      this.#showError('Revisa los nombres y valores de variantes configurados en la sección.');
    } else {
      this.#hideError();
    }
  }

  #formatMoney(cents) {
    const currency = this.dataset.currency || 'COP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-CO', {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'COP' ? 0 : 2,
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${Number(cents || 0) / 100} ${currency}`;
    }
  }

  #showError(message) {
    if (!this.errorNode) return;
    this.errorNode.classList.remove('hidden');
    this.errorNode.textContent = message;
  }

  #hideError() {
    if (!this.errorNode) return;
    this.errorNode.classList.add('hidden');
    this.errorNode.textContent = '';
  }

  #setValue(selector, value) {
    const input = this.querySelector(selector);
    if (input) input.value = value;
  }

  #setText(selector, value) {
    const node = this.querySelector(selector);
    if (node) node.textContent = value;
  }
}

if (!customElements.get('jd1-amaguau-personalizer')) {
  customElements.define('jd1-amaguau-personalizer', JD1AmaguauPersonalizer);
}